import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import "./TransactionForm.css";
import toast from "react-hot-toast";
import FieldSelectorModal from "./FieldSelectorModal";

const TransactionForm = () => {
  const { t } = useTranslation();
  const { fieldId, entityId, transactionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, token } = useContext(UserContext);

  const isEditMode = Boolean(transactionId);
  const pageTitle = isEditMode ? t("Edit Transaction") : t("Add New Payment");
  // Get the initial transaction type and entity from navigation state
  const transactionTypeFromState = location.state?.type;
  const entityFromState = location.state?.entity;
  const accountTypeFromState = location.state?.accountType;
  const showInvoiceSelectionFromState = location.state?.showInvoiceSelection;
  const selectedInvoiceFromState = location.state?.selectedInvoice;
  const prefilledDataFromState = location.state?.prefilledData;

  const [formData, setFormData] = useState({
    amount: "",
    date: new Date().toISOString().split("T")[0],
    category: "",
    description: "",
    // Loan-specific fields
    relatedParty: "",
    interestRate: "",
    dueDate: "",
  });
  const [activeType, setActiveType] = useState("");
  const [loading, setLoading] = useState(isEditMode);
  const [showInvoiceSelection, setShowInvoiceSelection] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showFieldSelection, setShowFieldSelection] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldCostDistribution, setFieldCostDistribution] = useState({});
  const [distributionMethod, setDistributionMethod] = useState("equality"); // equality, manual, percentage
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [availableLoans, setAvailableLoans] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [showFieldSelectorModal, setShowFieldSelectorModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const transactionTypes = [
    { key: "expense", label: t("Expense") },
    { key: "income", label: t("Income") },
    { key: "takenLoan", label: t("Loan Taken") },
    { key: "givenLoan", label: t("Loan Given") },
    { key: "loanRepayment", label: t("Loan Repayment") },
    { key: "repaymentReceived", label: t("Repayment Received") },
  ];
  // This useEffect handles populating the form for editing
  useEffect(() => {
    // Helper function to populate the form state from a transaction object
    const populateForm = (transactionObject) => {
      const paymentData = transactionObject;

      if (!paymentData) {
        console.error(
          "Transaction object is missing 'payment' property.",
          transactionObject
        );
        toast.error(t("Invalid transaction data provided."));
        navigate(-1);
        return;
      }

      let uiType = paymentData.type;
      if (paymentData.type === "sale") uiType = "income";
      if (paymentData.type === "purchase") uiType = "expense";

      setFormData({
        amount: paymentData.amountPaid || paymentData.value || "",
        date: new Date(paymentData.paymentDate || paymentData.date)
          .toISOString()
          .split("T")[0],
        category: paymentData.category || "",
        description: paymentData.description || paymentData.details || "",
        relatedParty:
          paymentData.relatedParty || paymentData.loan?.relatedParty || "",
        interestRate:
          paymentData.interestRate || paymentData.loan?.interestRate || "",
        dueDate: paymentData.dueDate
          ? new Date(paymentData.dueDate).toISOString().split("T")[0]
          : "",
      });

      // Set invoice, loan, employee, asset if present
      if (paymentData.invoiceId) {
        setShowInvoiceSelection(true);
        setSelectedInvoice({ id: paymentData.invoiceId });
      }
      if (paymentData.loanId) {
        setSelectedLoan({ id: paymentData.loanId });
      }
      if (paymentData.fieldId) {
        setShowFieldSelection(true);
        setSelectedFields([{ id: paymentData.fieldId }]);
      }
      setActiveType(uiType);
    };

    const transactionFromState = location.state?.transaction;

    if (isEditMode) {
      // If we are editing, we MUST have the transaction data from the state
      if (transactionFromState) {
        populateForm(transactionFromState);
      } else {
        // Handle the case where the state is missing (e.g., page refresh)
        toast.error(
          t("Transaction data is missing. Returning to the previous page.")
        );
        navigate(-1); // Go back to the field details page
      }
    } else {
      // For new transactions, set the type from state if provided
      // Otherwise, let the user select the type
      if (transactionTypeFromState) {
        setActiveType(transactionTypeFromState);
      }

      // Handle invoice payment from InvoiceDetails
      if (showInvoiceSelectionFromState && selectedInvoiceFromState) {
        setShowInvoiceSelection(true);
        setSelectedInvoice(selectedInvoiceFromState);
        setAvailableInvoices([selectedInvoiceFromState]); // Add the invoice to available list
      }

      // Set prefilled data if provided
      if (prefilledDataFromState) {
        setFormData((prev) => ({
          ...prev,
          ...prefilledDataFromState,
        }));
      }
      // If no type is provided, activeType remains empty and user can select
    }
  }, [isEditMode, transactionTypeFromState, location.state, navigate, t]);

  // Fetch available loans, invoices, employees, and assets when component mounts or transaction type changes
  useEffect(() => {
    const fetchAvailableData = async () => {
      if (!token || !userData) return;

      try {
        // Fetch loans for repayment selection - using correct API endpoint
        if (
          activeType === "loanRepayment" ||
          activeType === "repaymentReceived"
        ) {
          const loanType = activeType === "loanRepayment" ? "taken" : "given";
          const response = await axios.get(
            `/api/loan/findByFarmId/${userData.Farm?.id}`,
            {
              headers: { Authorization: token },
              params: {
                type: loanType,
              },
            }
          );
          // API returns {items: loans} structure
          const loansData = response.data?.items || response.data || [];

          // Filter out fully paid loans (where remaining amount is 0 or negative)
          const activeLoans = Array.isArray(loansData)
            ? loansData.filter((loanItem) => {
                const loan = loanItem.loan || loanItem;
                const amount = parseFloat(loan.amount || loan.totalAmount || 0);
                const paidAmount = parseFloat(
                  loanItem.loanPaidAmount || loan.paidAmount || 0
                );
                const remainingAmount = amount - paidAmount;
                return remainingAmount > 0; // Only show loans with positive remaining balance
              })
            : [];

          setAvailableLoans(activeLoans);
        }

        // Fetch invoices for invoice payment selection - using correct API endpoint
        if (
          (activeType === "income" || activeType === "expense") &&
          showInvoiceSelection
        ) {
          // Don't fetch if we already have a selected invoice from navigation state
          // This preserves the invoice passed from InvoiceDetails
          if (!selectedInvoiceFromState) {
            const invoiceType = activeType === "income" ? "sale" : "purchase";
            const response = await axios.get(
              `/api/invoice/${invoiceType}/${userData.Tenant?.id}/${userData.Farm?.id}`,
              {
                headers: { Authorization: token },
              }
            );
            // API returns {items: invoices} structure
            const invoicesData = response.data?.items || response.data || [];
            // Filter for unpaid invoices - check amountPaid vs totalAmount
            const unpaidInvoices = Array.isArray(invoicesData)
              ? invoicesData.filter((inv) => {
                  const totalAmount = inv.totalAmount || inv.total || 0;
                  const amountPaid = inv.amountPaid || 0;
                  return amountPaid < totalAmount;
                })
              : [];
            setAvailableInvoices(unpaidInvoices);
          } else {
            // Keep the selected invoice in the available list
            // This ensures it appears in the selector even if it's paid/overpaid
            setAvailableInvoices([selectedInvoiceFromState]);
          }
        }
      } catch (error) {
        console.error("Error fetching available data:", error);
        // Silently fail - these are optional features
      }
    };

    fetchAvailableData();
  }, [
    activeType,
    showInvoiceSelection,
    token,
    userData,
    selectedInvoiceFromState,
  ]);

  const resetConditionalFields = (newType) => {
    // Reset invoice-related fields (but preserve if coming from InvoiceDetails)
    if (!showInvoiceSelectionFromState) {
      setShowInvoiceSelection(false);
      setSelectedInvoice(null);
    }

    // Reset field-related fields
    setShowFieldSelection(false);
    setSelectedFields([]);
    setFieldCostDistribution({});

    // Reset loan-related fields
    setSelectedLoan(null);

    // Reset form data for conditional fields
    setFormData((prev) => ({
      ...prev,
      relatedParty: "",
      interestRate: "",
      dueDate: "",
    }));

    // Update visibility based on new type (matching Android's updateFieldsBasesOnSelectedType)
    if (newType === "income" || newType === "expense") {
      // Invoice and Field checkboxes will be shown in JSX based on activeType
    } else if (newType === "takenLoan" || newType === "givenLoan") {
      // Loan fields will be shown in JSX based on activeType
    } else if (newType === "loanRepayment" || newType === "repaymentReceived") {
      // Loan selection will be shown in JSX based on activeType
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - Check if transaction type is selected
    if (!activeType) {
      toast.error(t("Please select a transaction type"));
      return;
    }

    // Validation
    if (
      !formData.amount ||
      !formData.date ||
      !formData.category ||
      !formData.description
    ) {
      toast.error(t("Please fill in all required fields"));
      return;
    }

    // Additional validation for loan transactions
    if (
      (activeType === "takenLoan" || activeType === "givenLoan") &&
      (!formData.relatedParty || !formData.dueDate)
    ) {
      toast.error(
        t(
          "Loan details are incomplete. Please fill in related party and due date."
        )
      );
      return;
    }

    // Validation for loan repayments
    if (
      (activeType === "loanRepayment" || activeType === "repaymentReceived") &&
      !selectedLoan
    ) {
      toast.error(t("Please select a loan for repayment"));
      return;
    }

    // Validate field cost distribution
    if (showFieldSelection && selectedFields.length > 1) {
      const totalDistribution = Object.values(fieldCostDistribution).reduce(
        (sum, val) => sum + parseFloat(val || 0),
        0
      );
      if (distributionMethod === "percentage") {
        if (Math.abs(totalDistribution - 100) > 0.01) {
          toast.error(t("Field cost percentages must equal 100%"));
          return;
        }
      } else if (distributionMethod === "manual") {
        if (Math.abs(totalDistribution - parseFloat(formData.amount)) > 0.01) {
          toast.error(t("Field costs must equal transaction amount"));
          return;
        }
      }
      // For equality, distribution is automatic and always valid
    }

    // Prevent double submission
    if (submitting) {
      return;
    }

    // --- REAL API INTEGRATION for saving the transaction ---
    const farmId = userData?.Farm?.id;
    const tenantId = userData?.Tenant?.id;

    if (!farmId || !tenantId) {
      toast.error(t("Session invalid. Please login again."));
      return;
    }

    // The API uses different enums for income/expense ('sale'/'purchase').
    // For other types, the key matches the API enum.
    let paymentType = activeType;
    if (activeType === "income") paymentType = "sale";
    if (activeType === "expense") paymentType = "purchase";

    // Build payload based on context (field or entity) - matching Android format
    const payload = {
      id: null, // For a new transaction, ID is null
      invoiceId: selectedInvoice?.id || null,
      loanId: selectedLoan?.id || null,
      description: formData.description,
      details: formData.description, // Android uses 'details' field
      paymentDate: new Date(formData.date).getTime(),
      date: new Date(formData.date).getTime(), // Android uses 'date' field
      amountPaid: parseFloat(formData.amount),
      value: parseFloat(formData.amount), // Android uses 'value' field
      tenantId: tenantId,
      farmId: farmId,
      type: paymentType,
      category: formData.category,
      // Loan-specific fields
      relatedParty: formData.relatedParty || null,
      interestRate: formData.interestRate
        ? parseFloat(formData.interestRate)
        : null,
      dueDate: formData.dueDate ? new Date(formData.dueDate).getTime() : null,
    };

    // Add context-specific data
    if (entityId && entityFromState) {
      // Entity transaction
      payload.accountId = parseInt(entityId);
      payload.accountType =
        accountTypeFromState ||
        entityFromState.accountType?.toLowerCase() ||
        "client";
    } else if (fieldId) {
      // Field transaction
      payload.fieldId = fieldId;
      payload.fieldData = [
        {
          fieldId: parseInt(fieldId),
          amountPaid: parseFloat(formData.amount),
        },
      ];
    } else if (showFieldSelection && selectedFields.length > 0) {
      // Handle multiple field selections with cost distribution
      // Convert to fieldData array format (matching Android implementation)
      payload.fieldData = [];

      selectedFields.forEach((field) => {
        let amountPaid;

        if (selectedFields.length === 1) {
          // Single field - assign full amount
          amountPaid = parseFloat(formData.amount);
        } else if (distributionMethod === "equality") {
          // Equal distribution
          amountPaid = parseFloat(formData.amount) / selectedFields.length;
        } else if (distributionMethod === "percentage") {
          // Percentage-based distribution
          const percentage = parseFloat(fieldCostDistribution[field.id] || 0);
          amountPaid = (parseFloat(formData.amount) * percentage) / 100;
        } else {
          // Manual distribution
          amountPaid = parseFloat(fieldCostDistribution[field.id] || 0);
        }

        payload.fieldData.push({
          fieldId: field.id,
          amountPaid: amountPaid,
        });
      });
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        // Based on: PUT /api/payment [cite: 1371]
        // The payload for update must include the transaction's ID. [cite: 1373]
        await axios.put(
          "/api/payment",
          { ...payload, id: parseInt(transactionId) },
          {
            headers: { Authorization: token },
          }
        );
        toast.success(t("Transaction updated successfully!"));
      } else {
        // Based on: POST /api/payment [cite: 1323]
        await axios.post("/api/payment", payload, {
          headers: { Authorization: token },
        });
        toast.success(t("Transaction created successfully!"));
      }
      // Navigate back to the previous page on success
      navigate(-1);
    } catch (err) {
      console.error("Failed to save transaction:", err);
      toast.error(t("Failed to save transaction. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="transaction-form-container">
      <div className="transaction-form-card">
        <div className="transaction-form-header">
          <h1>{pageTitle}</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="transaction-form-group">
            <label>{t("Transaction Type")}</label>
            <div className="type-selector-container">
              {transactionTypes.map((typeInfo) => (
                <button
                  key={typeInfo.key}
                  type="button"
                  className={`type-button ${
                    activeType === typeInfo.key ? "active" : ""
                  }`}
                  onClick={() => {
                    if (!isEditMode && !transactionTypeFromState) {
                      setActiveType(typeInfo.key);
                      // Reset conditional fields when switching transaction types (Android behavior)
                      resetConditionalFields(typeInfo.key);
                    }
                  }}
                  disabled={
                    isEditMode ||
                    (transactionTypeFromState && activeType !== typeInfo.key)
                  }
                >
                  {typeInfo.label}
                </button>
              ))}
            </div>
          </div>

          {/* Amount field - always visible */}
          <div className="transaction-form-group">
            <label htmlFor="amount">{t("Amount")}</label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              required
            />
          </div>

          {/* Invoice selection checkbox - only for Income/Expense */}
          {(activeType === "income" || activeType === "expense") && (
            <div className="transaction-form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={showInvoiceSelection}
                  onChange={(e) => {
                    // Don't allow unchecking if coming from invoice details
                    if (!showInvoiceSelectionFromState) {
                      setShowInvoiceSelection(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedInvoice(null);
                      }
                    }
                  }}
                  disabled={showInvoiceSelectionFromState}
                />
                <span>{t("Invoice Payment")}</span>
              </label>
            </div>
          )}

          {/* Invoice selection dropdown */}
          {showInvoiceSelection && (
            <div className="transaction-form-group">
              <label htmlFor="invoiceSelection">{t("Select Invoice")}</label>
              <select
                id="invoiceSelection"
                value={selectedInvoice?.id || ""}
                onChange={(e) => {
                  // Don't allow changing if coming from invoice details
                  if (!showInvoiceSelectionFromState) {
                    const invoiceId = parseInt(e.target.value);
                    const invoice = availableInvoices.find(
                      (i) => i.id === invoiceId
                    );
                    setSelectedInvoice(invoice);
                    if (invoice) {
                      setFormData((prev) => ({
                        ...prev,
                        category: t("Invoice Payment"),
                        description: `${t("Invoice")} #${
                          invoice.invoiceNumber || invoice.id
                        }`,
                      }));
                    }
                  }
                }}
                disabled={showInvoiceSelectionFromState}
              >
                <option value="">{t("Select an invoice")}</option>
                {availableInvoices.map((invoice) => {
                  // Handle invoice data structure from API
                  const totalAmount = invoice.totalAmount || invoice.total || 0;
                  const amountPaid = invoice.amountPaid || 0;
                  const balanceDue = totalAmount - amountPaid;
                  const entityName =
                    invoice.supplier?.name ||
                    invoice.client?.name ||
                    invoice.clientName ||
                    invoice.entityName ||
                    t("Client");
                  return (
                    <option key={invoice.id} value={invoice.id}>
                      #{invoice.number || invoice.invoiceNumber || invoice.id} -{" "}
                      {entityName} - {totalAmount.toFixed(2)}
                      {balanceDue > 0
                        ? ` (${t("Balance")}: ${balanceDue.toFixed(2)})`
                        : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Field selection checkbox - only for Income/Expense */}
          {(activeType === "income" || activeType === "expense") &&
            !fieldId && (
              <div className="transaction-form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={showFieldSelection}
                    onChange={(e) => {
                      setShowFieldSelection(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedFields([]);
                        setFieldCostDistribution({});
                      }
                    }}
                  />
                  <span>{t("Fields Costs")}</span>
                </label>
              </div>
            )}

          {/* Field selection and cost distribution */}
          {showFieldSelection && (
            <>
              <div className="transaction-form-group">
                <label>{t("Select Fields")}</label>
                <div className="field-selection-info">
                  {t("Select one or more fields for cost distribution")}
                </div>
                {/* This would be replaced with a proper field selector component */}
                <button
                  type="button"
                  className="select-fields-button"
                  onClick={() => setShowFieldSelectorModal(true)}
                >
                  {selectedFields.length > 0
                    ? `${selectedFields.length} ${t("fields selected")}`
                    : t("Click to select fields")}
                </button>
              </div>

              {/* Cost distribution for multiple fields */}
              {selectedFields.length > 1 && (
                <div className="transaction-form-group">
                  <label>{t("Cost Distribution Method")}</label>
                  <div className="distribution-methods">
                    <button
                      type="button"
                      className={`method-button ${
                        distributionMethod === "equality" ? "active" : ""
                      }`}
                      onClick={() => {
                        setDistributionMethod("equality");
                        // Auto-calculate equal distribution
                        const equalAmount =
                          parseFloat(formData.amount || 0) /
                          selectedFields.length;
                        const newDistribution = {};
                        selectedFields.forEach((field) => {
                          newDistribution[field.id] = equalAmount.toFixed(2);
                        });
                        setFieldCostDistribution(newDistribution);
                      }}
                    >
                      {t("Equality")}
                    </button>
                    <button
                      type="button"
                      className={`method-button ${
                        distributionMethod === "manual" ? "active" : ""
                      }`}
                      onClick={() => {
                        setDistributionMethod("manual");
                        const newDistribution = {};
                        selectedFields.forEach((field) => {
                          newDistribution[field.id] = "";
                        });
                        setFieldCostDistribution(newDistribution);
                      }}
                    >
                      {t("Manual")}
                    </button>
                    <button
                      type="button"
                      className={`method-button ${
                        distributionMethod === "percentage" ? "active" : ""
                      }`}
                      onClick={() => {
                        setDistributionMethod("percentage");
                        const newDistribution = {};
                        selectedFields.forEach((field) => {
                          newDistribution[field.id] = "";
                        });
                        setFieldCostDistribution(newDistribution);
                      }}
                    >
                      {t("Percentage")}
                    </button>
                  </div>

                  {/* Field cost inputs */}
                  <div className="field-costs-list">
                    {selectedFields.map((field) => (
                      <div key={field.id} className="field-cost-item">
                        <label>{field.name}</label>
                        <input
                          type="number"
                          value={fieldCostDistribution[field.id] || ""}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (distributionMethod === "equality") {
                              // Recalculate all fields when one is changed in equality mode
                              const total = parseFloat(formData.amount || 0);
                              const otherFieldsCount =
                                selectedFields.length - 1;
                              const thisValue = parseFloat(value || 0);
                              const remainingAmount = total - thisValue;
                              const otherFieldAmount =
                                otherFieldsCount > 0
                                  ? (
                                      remainingAmount / otherFieldsCount
                                    ).toFixed(2)
                                  : 0;

                              const newDistribution = {};
                              selectedFields.forEach((f) => {
                                if (f.id === field.id) {
                                  newDistribution[f.id] = value;
                                } else {
                                  newDistribution[f.id] = otherFieldAmount;
                                }
                              });
                              setFieldCostDistribution(newDistribution);
                            } else {
                              setFieldCostDistribution((prev) => ({
                                ...prev,
                                [field.id]: value,
                              }));
                            }
                          }}
                          placeholder={
                            distributionMethod === "percentage" ? "0%" : "0.00"
                          }
                          step={
                            distributionMethod === "percentage" ? "1" : "0.01"
                          }
                          min="0"
                          max={
                            distributionMethod === "percentage"
                              ? "100"
                              : undefined
                          }
                          disabled={
                            distributionMethod === "equality" &&
                            selectedFields[0]?.id !== field.id
                          }
                        />
                        {distributionMethod === "percentage" && <span>%</span>}
                      </div>
                    ))}
                    <div className="field-cost-total">
                      <strong>{t("Total")}:</strong>
                      <span
                        className={
                          distributionMethod === "percentage"
                            ? Math.abs(
                                Object.values(fieldCostDistribution).reduce(
                                  (sum, val) => sum + parseFloat(val || 0),
                                  0
                                ) - 100
                              ) < 0.01
                              ? "valid"
                              : "invalid"
                            : Math.abs(
                                Object.values(fieldCostDistribution).reduce(
                                  (sum, val) => sum + parseFloat(val || 0),
                                  0
                                ) - parseFloat(formData.amount || 0)
                              ) < 0.01
                            ? "valid"
                            : "invalid"
                        }
                      >
                        {distributionMethod === "percentage"
                          ? `${Object.values(fieldCostDistribution)
                              .reduce(
                                (sum, val) => sum + parseFloat(val || 0),
                                0
                              )
                              .toFixed(0)}%`
                          : Object.values(fieldCostDistribution)
                              .reduce(
                                (sum, val) => sum + parseFloat(val || 0),
                                0
                              )
                              .toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Date field - always visible */}
          <div className="transaction-form-group">
            <label htmlFor="date">{t("Date")}</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          {/* Category field - always visible */}
          <div className="transaction-form-group">
            <label htmlFor="category">{t("Category")}</label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              placeholder={t("e.g., Labor, Sales")}
              required
            />
          </div>

          {/* Loan-specific fields - only for Taken/Given Loan */}
          {(activeType === "takenLoan" || activeType === "givenLoan") && (
            <>
              <div className="transaction-form-group">
                <label htmlFor="relatedParty">{t("Related Party")}</label>
                <input
                  type="text"
                  id="relatedParty"
                  name="relatedParty"
                  value={formData.relatedParty}
                  onChange={handleChange}
                  placeholder={t("Name of the party")}
                  required
                />
              </div>
              <div className="transaction-form-group">
                <label htmlFor="dueDate">{t("Due Date")}</label>
                <input
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="transaction-form-group">
                <label htmlFor="interestRate">{t("Interest Rate (%)")}</label>
                <input
                  type="number"
                  id="interestRate"
                  name="interestRate"
                  value={formData.interestRate}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  max="100"
                />
              </div>
            </>
          )}

          {/* Loan selection for repayments - only for Loan Repayment/Repayment Received */}
          {(activeType === "loanRepayment" ||
            activeType === "repaymentReceived") && (
            <div className="transaction-form-group">
              <label htmlFor="loanSelection">{t("Select Loan")}</label>
              <select
                id="loanSelection"
                value={selectedLoan?.id || ""}
                onChange={(e) => {
                  const loanId = parseInt(e.target.value);
                  const loanItem = availableLoans.find((l) => {
                    const lData = l.loan || l;
                    return lData.id === loanId;
                  });
                  if (loanItem) {
                    const loanData = loanItem.loan || loanItem;
                    setSelectedLoan(loanData);
                    setFormData((prev) => ({
                      ...prev,
                      category: t("Loan Repayment"),
                      description: `${t("Loan Repayment")} - ${
                        loanData.relatedParty ||
                        loanData.description ||
                        t("Loan")
                      }`,
                    }));
                  }
                }}
                required
              >
                <option value="">
                  {availableLoans.length === 0
                    ? t("No active loans available")
                    : t("Select a loan")}
                </option>
                {availableLoans.map((loan) => {
                  // Handle the loan data structure from API
                  const loanData = loan.loan || loan;
                  const remainingAmount =
                    loan.remainingAmount ||
                    parseFloat(loanData.amount || loanData.totalAmount || 0) -
                      parseFloat(loan.loanPaidAmount || loan.paidAmount || 0);

                  // Only show loans with remaining balance
                  if (remainingAmount <= 0) return null;

                  return (
                    <option key={loanData.id} value={loanData.id}>
                      {loanData.relatedParty || t("Loan")} -{" "}
                      {loanData.amount || loanData.totalAmount}
                      {` (${t("Remaining")}: ${remainingAmount.toFixed(2)})`}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Details field - always visible at the bottom */}
          <div className="transaction-form-group">
            <label htmlFor="description">{t("Details")}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder={t("Add a note...")}
              required
            ></textarea>
          </div>

          {/* Form Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="save-button"
              disabled={submitting}
            >
              {submitting ? t("Saving...") : t("Save")}
            </button>
          </div>
        </form>
      </div>

      {/* Field Selector Modal */}
      <FieldSelectorModal
        isOpen={showFieldSelectorModal}
        onClose={() => setShowFieldSelectorModal(false)}
        onFieldsSelected={(fields) => {
          setSelectedFields(fields);
          // If multiple fields selected, initialize distribution
          if (fields.length > 1 && distributionMethod === "equality") {
            const equalAmount =
              parseFloat(formData.amount || 0) / fields.length;
            const newDistribution = {};
            fields.forEach((field) => {
              newDistribution[field.id] = equalAmount;
            });
            setFieldCostDistribution(newDistribution);
          }
        }}
        multiSelect={true}
        selectedFieldIds={selectedFields.map((f) => f.id)}
      />
    </div>
  );
};

export default TransactionForm;
